import { isInstance, notEmpty } from "@de-xima/xima-common-js-lang";
import jQuery from "jquery";

import type { IComponentHandler, IComponentHandlerCallbacks, IInsertData } from "../../Header.ts";
import { findWidgetForId } from "../../Util.ts";
import { AbstractComponentHandler } from "../AbstractComponentHandler.ts";

/**
 * Handler for input components of type `primefacesExtensions:ckEditor`.
 */
export class FcHtmlEditorHandler extends AbstractComponentHandler implements IComponentHandler {
  override accepts($element: JQuery): boolean {
    if ($element.find(".cke").length === 1) {
      return true;
    }

    // Element with class cke does not exist yet when CKEDITOR was not loaded yet.
    const $textarea = $element.find("textarea");
    const widget = findWidgetForId($textarea.attr("id") ?? "", PrimeFaces.widget.BaseWidget);

    // Widget was initialized already.
    if (widget !== undefined) {
      return isInstance(widget, PrimeFaces.widget.FcHtmlEditor);
    }

    // Widget not initialized, check type of widget from script tag.
    return $textarea.next().text().indexOf("FcHtmlEditor") >= 0;
  }

  override focus($element: JQuery): void {
    const ck = this._getCkEditor($element);
    ck?.focus();
  }

  override getInputElement($element: JQuery): JQuery {
    const ck = this._getCkEditor($element);
    return ck !== undefined ? jQuery(ck.widgets.editor.container.$) : jQuery();
  }

  override initialize($element: JQuery, callbacks: IComponentHandlerCallbacks): boolean {
    if (!("FcHtmlEditor" in PrimeFaces.widget)) {
      return false;
    }
    // Check if the CKEDITOR was initialized
    if (this._isReady($element)) {
      this._initialize($element, callbacks);
      return true;
    }
    // Otherwise, we need to wait for the CKEditor to load.
    const observer = new MutationObserver(() => {
      if (this._isReady($element)) {
        // We do not need to listen to any further changes, so unregister for performance.
        observer.disconnect();
        this._initialize($element, callbacks);
      }
    });
    const target = $element.get(0);
    if (target !== undefined) {
      observer.observe(target, {
        childList: true,
        subtree: true,
      });
    }
    return true;
  }

  override insertText($element: JQuery, data: IInsertData): void {
    const ck = this._getCkEditor($element);
    if (ck !== undefined) {
      const selection = ck.getSelection();
      const firstRange = selection.getRanges()[0];
      const selectedText = ck.getSelection().getSelectedText();
      if (notEmpty(selectedText) && firstRange !== undefined) {
        switch (data.insertHint) {
          case "html":
            // Just insert the HTML content at the current selection
            // Do not reselect the inserted text (it might be a long section of HTML)
            firstRange.deleteContents();
            ck.insertHtml(data.content);
            break;
          case "link": {
            const anchor = ck.document.createElement("a");
            anchor.setAttribute("href", data.content);
            anchor.setText(data.content);
            firstRange.deleteContents();
            ck.insertElement(anchor);
            break;
          }
          case "text": {
            // When text is selected: replace the selection and select the inserted text
            const current = firstRange.startContainer.getText();
            const pre = current.substring(0, firstRange.startOffset);
            const post = current.substring(firstRange.endOffset);
            const newText = pre + data.content + post;
            firstRange.startContainer.setText(newText);
            const newRange = ck.createRange();
            newRange.setStart(firstRange.startContainer, firstRange.startOffset);
            newRange.setEnd(firstRange.startContainer, firstRange.startOffset + data.content.length);
            ck.getSelection().selectRanges([newRange]);
            break;
          }
        }
      } else {
        // When no text is selected: just insert the text at the current cursor position
        switch (data.insertHint) {
          case "html":
            ck.insertHtml(data.content);
            break;
          case "link": {
            const anchor = ck.document.createElement("a");
            anchor.setAttribute("href", data.content);
            anchor.setText(data.content);
            ck.insertElement(anchor);
            break;
          }
          case "text":
            ck.insertText(`${data.content} `);
            break;
        }
      }
    }
  }

  /**
   * @param $element Input component.
   * @returns The ckeditor instance.
   */
  private _getCkEditor($element: JQuery): CKEDITOR.editor | undefined {
    const $cke = $element.find(".cke");
    if ($cke.length !== 1 || CKEDITOR === undefined) {
      return undefined;
    }
    return CKEDITOR.instances[($cke.attr("id") ?? "").substring(4)];
  }

  /**
   * Same as the initialize method, but caled once the ckeditor is ready.
   * @param $element CKEditor input element.
   * @param callbacks Callbacks we should invoke.
   */
  private _initialize($element: JQuery, callbacks: IComponentHandlerCallbacks): void {
    const ck = this._getCkEditor($element);
    const CTRL_SPACE = CKEDITOR.CTRL | " ".charCodeAt(0);
    if (ck !== undefined) {
      ck.addCommand("placeholder", {
        exec() {
          callbacks.onPlaceholderRequested();
          return true;
        },
      });
      ck.setKeystroke(CTRL_SPACE, "placeholder");
      ck.on("focus", () => callbacks.onFocus());
    }
  }

  /**
   * @param $element Input component.
   * @returns Whether the CKEDITOR instance was already initialized.
   */
  private _isReady($element: JQuery): boolean {
    return this._getCkEditor($element) !== undefined;
  }
}
